import { SearchbarCustomEvent } from '@ionic/core';
import { Component, Element, Event, EventEmitter, Fragment, h, Prop, State, Watch } from '@stencil/core';
import { RequestAccess } from '../../components/concourse-login/share-link/request-access';
import { ShareLinkModal } from '../../components/concourse-login/share-link/share-link-modal';
import { User } from '../../global/models';
import { CompanyDoc } from 'common/dist/types/models/Project';

/**
 * The Concourse Header has some logic for long titles, since these will often conflict with the search bar.
 * We implemented a ticket approach to long titles, and suggest that we always use the 'search button'
 * instead of 'search field' when the title contains user generated content.
 *
 * We also implemented a click option on the ticker header to expand the title to the full width of the screen.
 * This is so that mobile devices etc can see the full title easily instead of waiting for the ticker to traverse.
 */
@Component({
  tag: 'concourse-header',
  styleUrl: 'concourse-header.css',
  //shadow: true,
})
export class ConcourseHeader {
  @Element() el: HTMLElement;

  @Prop() projectId: string;
  @Prop() pageTitle: string = 'Concourse';
  @Prop() shareRoles: string[];
  @Prop() searchValue?: string;
  @Prop() color: string = 'var(--ion-background-color)';

  @Prop() showLogo: boolean = false;
  @Prop() showBack: boolean = false;
  @Prop() showHamburger: boolean = false;
  @Prop() showSearchField: boolean = false;
  @Prop() showSearchButton: boolean = false;

  @State() shareModalOpen: boolean;
  @State() searchBarWidth: number = 0;

  // Ticker for the title
  @State() tickerActive = false;
  @State() tickerDistance = 0;
  @State() tempFullWidthTitle = false;

  @Event() searchInput: EventEmitter<string>;
  @Event() searchDismiss: EventEmitter<void>;
  @Event() enterClicked: EventEmitter<void>;
  @Event() back: EventEmitter<void>;

  private titleClipRef?: HTMLDivElement;
  private overflowDelayTimeout?: NodeJS.Timeout;

  /** Light-DOM span; ion-title’s shadow inner overflow makes host scrollWidth unreliable. */
  private titleMeasureRef?: HTMLSpanElement;

  get user(): User {
    return globalThis.user;
  }

  get company(): CompanyDoc {
    return globalThis.company;
  }

  componentDidRender() {
    // need a timeout to ensure the scrollWidth is calculated
    this.checkOverflowDelayed();
  }

  checkOverflowDelayed() {
    if (!Object.keys(this).includes('overflowDelayTimeout')) return;
    if (this.overflowDelayTimeout) {
      clearTimeout(this.overflowDelayTimeout);
      this.overflowDelayTimeout = undefined;
    }
    this.overflowDelayTimeout = setTimeout(() => this.checkOverflow(), 5000);
  }

  @Watch('pageTitle')
  pageTitleChanged() {
    this.checkOverflowDelayed();
  }

  private fullTitleTimeout?: NodeJS.Timeout;
  private overflowTimeout?: ReturnType<typeof setTimeout>;
  onTitleClick() {
    // if ticket active, termporarily expand the width titleClipRef to the full width of the screen
    if (this.fullTitleTimeout) {
      clearTimeout(this.fullTitleTimeout);
      this.fullTitleTimeout = undefined;
      this.tempFullWidthTitle = false;
    } else if (this.tickerActive) {
      this.tempFullWidthTitle = true;
      this.fullTitleTimeout = setTimeout(() => {
        if (!Object.keys(this).includes('tempFullWidthTitle')) return;
        this.tempFullWidthTitle = false;
        this.checkOverflowDelayed();
      }, 5000);
      this.checkOverflowDelayed();
    }
  }

  onSearchButtonClick(event: MouseEvent) {
    const button = event.target as HTMLIonButtonElement;

    console.log(button.getBoundingClientRect());
    // get rigth edge of button relative to the window
    const rightEdge = button.getBoundingClientRect().left + button.getBoundingClientRect().width;
    // get left edge of the screen
    this.searchBarWidth = rightEdge < 400 ? this.el.clientWidth : rightEdge;
    setTimeout(() => this.focusSearchField(), 100);
  }

  async focusSearchField() {
    const input = this.el.querySelector('.cc-searchbar-mobile') as HTMLIonInputElement;
    input.value = '';
    input.focus();

    input.getInputElement().then(input => {
      input.value = '';
      input.focus();
    });
  }

  async showShareModal() {
    if (!this.user.canAdministerPeople(this.projectId)) {
      RequestAccess.presentModal(this.projectId, 'Share Project', `p-${this.projectId}-people-write`, () => {
        console.log('access requested');
      });
      return;
    }
    this.shareModalOpen = true;
    const role = this.shareRoles[0];
    ShareLinkModal.present(this.projectId, this.pageTitle, role, () => {
      this.shareModalOpen = false;
    });
  }

  private checkOverflow() {
    if (!Object.keys(this).includes('titleClipRef') || !Object.keys(this).includes('titleMeasureRef')) return;
    const clip = this.titleClipRef;
    const textEl = this.titleMeasureRef;
    if (!clip || !textEl) return;
    if (clip.clientWidth === 0 || textEl.scrollWidth === 0) return;

    const availableWidth = clip.clientWidth;
    const fullWidth = textEl.scrollWidth;
    const overflow = fullWidth > availableWidth;
    const distance = overflow ? fullWidth - availableWidth : 0;

    if (this.tickerActive !== overflow || this.tickerDistance !== distance) {
      requestAnimationFrame(() => {
        this.tickerActive = overflow;
        this.tickerDistance = distance;
      });
    }
  }

  private onSearchInput(e: SearchbarCustomEvent) {
    this.searchInput.emit(e.detail.value);
  }

  private onKeyUp(e: KeyboardEvent) {
    // enter key clicked
    if (e.key === 'Enter') {
      this.enterClicked.emit();
    }
  }

  private onCancelClick() {
    this.searchBarWidth = 0;
    this.searchDismiss.emit();
  }

  private onClearIconClick() {
    this.searchInput.emit('');
  }

  async disconnectedCallback() {
    if (this.overflowDelayTimeout) {
      clearTimeout(this.overflowDelayTimeout);
      this.overflowDelayTimeout = undefined;
    }
    if (this.fullTitleTimeout) {
      clearTimeout(this.fullTitleTimeout);
      this.fullTitleTimeout = undefined;
    }
  }

  render() {
    const logoSrc = this.company?.logo ? `data:${this.company.logo.mimeType};base64,${this.company.logo.data}` : '/assets/concourse-icon-white.svg';
    const logoHeight = this.company?.logo ? this.company.logo.height : 42;
    const logoWidth = this.company?.logo ? this.company.logo.width : 48;
    return (
      <Fragment>
        <ion-header style={{ '--background': this.color }}>
          <ion-toolbar>
            <ion-buttons slot="start">
              {this.showBack ? (
                <ion-button data-testid="back-button" id="back-button-header" fill="clear" onClick={() => this.back.emit()}>
                  <ion-icon slot="icon-only" name="arrow-back" style={{ zoom: 1.3 }} />
                </ion-button>
              ) : (
                this.showHamburger && <ion-menu-button aria-label="menu" id="hamburger-button" style={{ zoom: 1.1 }}></ion-menu-button>
              )}
              {this.showLogo && <img class="concourse-logo" src={logoSrc} height={logoHeight} width={logoWidth} style={{ 'padding-right': '6px' }} />}
              {this.searchBarWidth === 0 && (
                <div
                  class={{ 'title-clip': true, 'ticker-active': this.tickerActive, 'full-width-title': this.tempFullWidthTitle }}
                  style={this.tickerActive && this.tickerDistance > 0 ? { '--ticker-distance': `${-this.tickerDistance}px`, 'background-color': this.color } : undefined}
                  ref={el => (this.titleClipRef = el as HTMLDivElement)}
                >
                  <ion-title class={this.tickerActive ? 'title-text ticker' : 'title-text'} onClick={() => this.onTitleClick()}>
                    <span class="title-measure" ref={el => (this.titleMeasureRef = el as HTMLSpanElement)}>
                      {this.pageTitle}
                    </span>
                  </ion-title>
                </div>
              )}
            </ion-buttons>
            <div class="center-content">
              {this.showSearchField && (
                <ion-searchbar
                  slot="center"
                  class="cc-searchbar ion-hide-md-down"
                  data-testid="searchbar-desktop"
                  value={this.searchValue ?? ''}
                  onIonInput={e => this.onSearchInput(e)}
                  onKeyUp={(e: KeyboardEvent) => this.onKeyUp(e)}
                  onIonClear={() => this.onClearIconClick()}
                ></ion-searchbar>
              )}
              <slot name="center"></slot>
            </div>
            <ion-buttons slot="end">
              {(this.showSearchField || this.showSearchButton) && this.searchBarWidth === 0 && (
                <ion-button data-testid="search-button" fill="clear" class={this.showSearchButton ? '' : 'ion-hide-md-up'} onClick={e => this.onSearchButtonClick(e)}>
                  <ion-icon slot="icon-only" name="search-outline"></ion-icon>
                </ion-button>
              )}
              {/* default slot for all the buttons */}
              <slot></slot>
              {this.shareRoles && (
                <ion-button id="share-button" data-testid="header-share-button" onClick={() => this.showShareModal()}>
                  <ion-icon slot="icon-only" class={this.shareModalOpen ? 'header-icon-selected' : 'header-icon'} name="share-social-outline"></ion-icon>
                </ion-button>
              )}
            </ion-buttons>
          </ion-toolbar>
          <ion-toolbar class="full-width-toolbar" style={{ display: this.searchBarWidth > 0 ? 'block' : 'none', width: this.searchBarWidth + 'px' }}>
            <ion-searchbar
              class="cc-searchbar-mobile"
              value={this.searchValue ?? ''}
              show-cancel-button="always"
              cancel-button-icon="close-circle"
              data-testid="searchbar-mobile"
              onIonFocus={() => this.searchInput.emit(this.searchValue)}
              onIonInput={e => this.onSearchInput(e)}
              onKeyUp={(e: KeyboardEvent) => this.onKeyUp(e)}
              onIonClear={() => this.onClearIconClick()}
              onIonCancel={() => this.onCancelClick()}
            ></ion-searchbar>
          </ion-toolbar>
        </ion-header>
      </Fragment>
    );
  }
}
